import { APIService, ArtButton, useApiService } from "@art/shared-ui";
import { CheckCircleOutline, Schedule } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ART_URL } from "../../../Constant";
import { usePageWrapper } from "../../../hooks";
import ResourceAccessDialog, {
  ResourceAccess,
  ResourceScope,
} from "../application-users/resource-access";
import { useAttributePermission } from "../../permissions";

type Project = { title: string; project_key: string; tenant_name: string };
type Environment = { title: string };

const environmentsForProject = (
  scopes: ResourceScope[],
  projectKey: string,
  environments: Environment[],
) => {
  const relevant = scopes.filter(
    (scope) => scope.project_key === "*" || scope.project_key === projectKey,
  );
  if (relevant.some((scope) => scope.environment_name === "*"))
    return environments.map((item) => item.title);
  return [...new Set(relevant.map((scope) => scope.environment_name))];
};

function ProjectAccessDetails() {
  const { projectKey = "" } = useParams();
  const navigate = useNavigate();
  const apiService = useApiService();
  const idpService = useMemo(
    () =>
      new APIService({
        ART_STORE: apiService.getStore(),
        BASE_URL: ART_URL + "/idp/api",
        DisableDefaultHeadder: true,
        DisableDefaultTenantUrl: true,
      }),
    [apiService],
  );
  const [project, setProject] = useState<Project | null>(null);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [access, setAccess] = useState<ResourceAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessEmail, setAccessEmail] = useState<string | null>(null);
  const canManageAccess = useAttributePermission(
    "PLATFORM_SETTINGS",
    "USER_MANAGEMENT",
    "ManageResourceAccess",
  );

  const load = () =>
    Promise.all([
      apiService.get("/projects/list-all"),
      apiService.get("/environments?limit=100"),
      idpService.get("/tenants/current/users/resource-access"),
    ])
      .then(([projectResponse, environmentResponse, accessResponse]: any[]) => {
        const projects = projectResponse.data?.data?.projects || [];
        setProject(
          projects.find((item: Project) => item.project_key === projectKey) ||
            null,
        );
        setEnvironments(environmentResponse.data?.data?.environments || []);
        setAccess(accessResponse.data?.data || []);
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, [projectKey]);

  const users = access
    .map((item) => ({
      ...item,
      environments: environmentsForProject(
        [
          ...(item.resource_scopes ?? []),
          ...(item.inherited_resource_scopes ?? []),
        ],
        projectKey,
        environments,
      ),
      inheritedEnvironments: environmentsForProject(
        item.inherited_resource_scopes ?? [],
        projectKey,
        environments,
      ),
    }))
    .filter((item) => item.environments.length > 0);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress />
      </Box>
    );

  const selectedAccess = access.find((item) => item.email === accessEmail);

  return (
    <Stack spacing={3}>
      <ResourceAccessDialog
        email={accessEmail || ""}
        existingScopes={selectedAccess?.resource_scopes}
        open={!!accessEmail}
        onClose={() => setAccessEmail(null)}
        onSaved={load}
      />
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <ArtButton
          buttonType="button"
          dataQa="back-projects"
          label="Projects"
          variant="outlined"
          onClick={() => navigate("/platform-settings/projects")}
        />
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {project?.title || projectKey}
          </Typography>
          <Typography color="text.secondary">
            {projectKey} · Resource access
          </Typography>
        </Box>
      </Box>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2.5, bgcolor: "action.hover" }}>
          <Typography variant="h6" fontWeight={700}>
            People with project access
          </Typography>
          <Typography color="text.secondary">
            Active users and pending invitations that include this project.
          </Typography>
        </Box>
        <Divider />
        {!users.length ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">
              No users are assigned to this project.
            </Typography>
          </Box>
        ) : (
          users.map((item, index) => (
            <Box key={item.email}>
              {index > 0 && <Divider />}
              <Box
                sx={{
                  px: 3,
                  py: 2.5,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(260px, 1fr) 2fr auto",
                  },
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {item.status === "active" ? (
                      <CheckCircleOutline color="success" fontSize="small" />
                    ) : (
                      <Schedule color="warning" fontSize="small" />
                    )}
                    <Typography fontWeight={650}>{item.email}</Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 3.5, textTransform: "capitalize" }}
                  >
                    {item.status}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {item.environments.map((environment) => (
                    <Chip
                      key={environment}
                      label={`${environment}${item.inheritedEnvironments.includes(environment) ? " · inherited" : ""}`}
                      size="small"
                      color={
                        item.inheritedEnvironments.includes(environment)
                          ? "secondary"
                          : "primary"
                      }
                      variant="outlined"
                    />
                  ))}
                </Stack>
                {canManageAccess && (
                  <ArtButton
                    buttonType="button"
                    dataQa="manage-project-access"
                    label="Manage access"
                    variant="outlined"
                    onClick={() => setAccessEmail(item.email)}
                  />
                )}
              </Box>
            </Box>
          ))
        )}
      </Paper>
    </Stack>
  );
}

export default usePageWrapper(ProjectAccessDetails, {
  page_title: "Project access",
});
